/**
 * Every record shows its own open obligations (pack-decisions over the stored facts) and its
 * links in the kernel graph. One tenant evaluation is shared by all panels for 30 s.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, Card, Empty, List, Space, Tag, Typography } from "antd";
import { ApartmentOutlined, ClockCircleOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { RELATED_CHANGED, scopeQs, useOrgScopeStore } from "@evo-flywheel/ui";
import { factTypes } from "@kit/spines";
import { kernel, type Evaluation, type Finding } from "../api";
import { FindingsTable } from "../findings";
import { resourceBySlug } from "../kit-resources";

let cached: { at: number; ev: Promise<Evaluation> } | undefined;
export function evaluation(force = false): Promise<Evaluation> {
  if (force || !cached || Date.now() - cached.at > 30_000) cached = { at: Date.now(), ev: kernel<Evaluation>("/decisions/evaluate-stored", {}) };
  return cached.ev;
}

/**
 * Organisation scope on obligations (governance ADR-037, C55). Rules always run over the whole
 * group's facts — an obligation can hang on a link across companies — and the findings are then
 * narrowed to subjects inside the chosen companies, with the same kernel filters the lists use
 * (direct organisation field, or through the parent record). Subjects of a module that is not per
 * company are group-wide: kept while "includi gli elementi di gruppo" is on.
 */
export async function scopeFindings(findings: Finding[]): Promise<Finding[]> {
  const { selected, withGroup } = useOrgScopeStore.getState();
  if (!selected.length) return findings;
  const keep = new Map<string, Set<string> | boolean>();
  await Promise.all([...new Set(findings.map((f) => f.subject.type))].map(async (type) => {
    const slug = factTypes[type]?.slug ?? type;
    const qs = scopeQs(slug);
    if (!qs) { keep.set(type, withGroup); return; }
    const ids = new Set<string>();
    for (let offset = 0; ; offset += 1000) {
      const r = await kernel<{ entities: Array<{ entity_id: string }> }>(`/entities?type=${encodeURIComponent(slug)}&limit=1000&offset=${offset}${qs}`);
      r.entities.forEach((e) => ids.add(e.entity_id));
      if (r.entities.length < 1000) break;
    }
    keep.set(type, ids);
  }));
  return findings.filter((f) => { const k = keep.get(f.subject.type); return k === true || (k instanceof Set && k.has(f.subject.id)); });
}

/** Re-render key for anything computed under the organisation scope. */
export function useScopeKey(): string {
  const { selected, withGroup } = useOrgScopeStore();
  return `${selected.join(",")}|${withGroup}`;
}

export function useFindings(subjectId?: string): Finding[] | undefined {
  const [out, setOut] = useState<Finding[]>();
  useEffect(() => {
    if (!subjectId) return;
    evaluation().then((ev) => setOut(ev.findings.filter((f) => f.subject.id === subjectId))).catch(() => setOut([]));
  }, [subjectId]);
  return out;
}

/** Field a statutory clock starts from (incident notification: detection time). */
const CLOCK_ANCHOR: Record<string, string> = { cmp_data_breach: "dbrdatetime" };

export const ObligationsCard: React.FC<{ slug?: string; record: Record<string, unknown> }> = ({ slug, record }) => {
  const findings = useFindings(record.id as string | undefined);
  const anchor = slug ? record[CLOCK_ANCHOR[slug]] : undefined;
  const unmet = findings?.filter((f) => f.status === "unmet") ?? [];
  const clocks = anchor ? unmet.filter((f) => f.deadlineHours) : [];
  return <Card size="small" style={{ marginTop: 16 }} loading={!findings}
    title={<Space><SafetyCertificateOutlined />Obblighi di questa scheda</Space>}
    extra={findings && <Tag color={unmet.length ? "red" : "green"}>{unmet.length ? `${unmet.length} da sanare` : "tutto soddisfatto"}</Tag>}>
    {clocks.length > 0 && <Space wrap style={{ marginBottom: 12 }}>
      {clocks.map((f) => {
        const due = dayjs(String(anchor)).add(f.deadlineHours!, "hour");
        const left = due.diff(dayjs(), "hour");
        return <Tag key={f.obligation} icon={<ClockCircleOutlined />} color={left < 0 ? "red" : left <= 24 ? "orange" : "gold"}>
          {f.obligation.replace(/_/g, " ")}: {left < 0 ? `scaduto da ${-left} h` : `entro ${left} h`} ({due.format("DD/MM HH:mm")})
        </Tag>;
      })}
    </Space>}
    {findings && findings.length === 0
      ? <Typography.Text type="secondary">Nessuna regola si applica a questa scheda.</Typography.Text>
      : findings && <FindingsTable findings={findings} compact labels={{ [String(record.id)]: "questa scheda" }} />}
  </Card>;
};

/** Fired by panels that add links (e.g. an audit snapshot) so the links panel reloads. */
export const LINKS_CHANGED = RELATED_CHANGED;

/** Kernel GET /graph/:id (GraphConcept.traverse). */
interface GraphResult { entities: Array<{ entity_id: string; entity_type: string; label: string }>; edges: Array<{ from_entity_id: string; to_entity_id: string; relation: string }> }

/** Links of the record in the kernel graph, grouped by relation, each opening its own record. */
export const LinksCard: React.FC<{ id: string }> = ({ id }) => {
  const [g, setG] = useState<GraphResult>();
  const [err, setErr] = useState<string>();
  useEffect(() => {
    const load = () => kernel<GraphResult>(`/graph/${encodeURIComponent(id)}?depth=1`).then(setG).catch((e) => setErr(e.message));
    load();
    window.addEventListener(LINKS_CHANGED, load);
    return () => window.removeEventListener(LINKS_CHANGED, load);
  }, [id]);
  const nodes = new Map((g?.entities ?? []).map((n) => [n.entity_id, n]));
  const rows = (g?.edges ?? []).filter((e) => e.from_entity_id === id || e.to_entity_id === id).map((e) => {
    const out = e.from_entity_id === id;
    const other = out ? e.to_entity_id : e.from_entity_id;
    const n = nodes.get(other);
    return { relation: e.relation, out, id: other, type: n?.entity_type ?? "", label: n?.label ?? other };
  }).filter((r) => r.id !== id).sort((a, b) => a.relation.localeCompare(b.relation) || a.label.localeCompare(b.label));
  return <Card size="small" style={{ marginTop: 16 }} title={<Space><ApartmentOutlined />Collegamenti</Space>} loading={!g && !err}>
    {err ? <Alert type="warning" showIcon message="Collegamenti non disponibili" description={err} />
      : rows.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nessun collegamento" />
        : <List size="small" dataSource={rows} renderItem={(r) => <List.Item>
            <Space><Tag>{r.out ? r.relation : `← ${r.relation}`}</Tag>
              {resourceBySlug[r.type] ? <Link to={`/${r.type}/show/${encodeURIComponent(r.id)}`}>{r.label}</Link> : r.label}
              <Typography.Text type="secondary">{resourceBySlug[r.type]?.meta.label ?? r.type}</Typography.Text></Space>
          </List.Item>} />}
  </Card>;
};
